import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, X, Check, Ban, RefreshCw } from 'lucide-react';
import type { Tutorial, TutorialVersion, VideoAssignment } from '@platform/shared-types';
import { videoTasksApi } from '../../services/videoTasksApi';
import { tutorialApi, tutorialCdnUrl, TutorialApiError } from '../../services/tutorialApi';
import { ConfirmDialog } from '../../components/finance/financeUi';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import { STATUS_LABEL, STATUS_TONE, mmss, formatDuration, isTranscriptNote } from './videoTaskUi';

const RENDER_POLL_MS = 4000;
const RENDER_POLL_MAX_ATTEMPTS = 90; // ~6 minutes, same budget as TutorialsTab

type ScriptView = 'script' | 'raw' | 'brief';

interface Props {
  assignmentId: string;
  tenantName: (tenantId?: string | null) => string;
  onBack: () => void;
  onChanged: (a: VideoAssignment) => void;
}

const isWorking = (s?: TutorialVersion['render']['state']) => s === 'none' || s === 'queued' || s === 'running';

const friendly = (e: any, fallback: string) => {
  if (e instanceof TutorialApiError && e.message === 'render_not_ready') {
    return 'The video has not finished rendering yet — approve once it plays here.';
  }
  return e?.message || fallback;
};

export default function VideoTaskReview({ assignmentId, tenantName, onBack, onChanged }: Props) {
  const [assignment, setAssignment] = useState<VideoAssignment | null>(null);
  const [tutorial, setTutorial] = useState<Tutorial | null>(null);
  const [versions, setVersions] = useState<TutorialVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [view, setView] = useState<ScriptView>('script');
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [tagDraft, setTagDraft] = useState('');
  const [busy, setBusy] = useState<'approve' | 'changes' | 'cancel' | 'meta' | 'script' | 'render' | null>(null);
  const [actionError, setActionError] = useState('');
  const [polling, setPolling] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const genRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const stopPoll = () => { if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null; } };

  const versionNo = assignment?.submission?.versionNo;
  const version = versions.find((v) => v.versionNo === versionNo) ?? null;
  const render = version?.render;
  const ready = render?.state === 'ready' && !!render.mp4Key;

  const versionNoRef = useRef<number | undefined>(undefined);
  useEffect(() => { versionNoRef.current = versionNo; }, [versionNo]);

  const poll = (tutorialId: string, gen: number, attempt: number) => {
    timerRef.current = window.setTimeout(async () => {
      if (gen !== genRef.current) return;
      try {
        const d = await tutorialApi.get(tutorialId);
        if (gen !== genRef.current) return;
        setTutorial(d.tutorial); setVersions(d.versions);
        const v = d.versions.find((x) => x.versionNo === versionNoRef.current);
        if (isWorking(v?.render.state) && attempt + 1 < RENDER_POLL_MAX_ATTEMPTS) poll(tutorialId, gen, attempt + 1);
        else setPolling(false);
      } catch {
        if (attempt + 1 < RENDER_POLL_MAX_ATTEMPTS) poll(tutorialId, gen, attempt + 1);
        else setPolling(false);
      }
    }, RENDER_POLL_MS);
  };
  useEffect(() => {
    genRef.current += 1;
    const gen = genRef.current;
    stopPoll();
    setLoading(true); setLoadError('');
    (async () => {
      try {
        // No single-task GET exists; the list is small and already carries every field.
        const { assignments } = await videoTasksApi.list();
        const a = assignments.find((x) => x.assignmentId === assignmentId);
        if (!a) throw new Error('This video task no longer exists.');
        const d = await tutorialApi.get(a.tutorialId);
        if (gen !== genRef.current) return;
        setAssignment(a); setTutorial(d.tutorial); setVersions(d.versions);
        versionNoRef.current = a.submission?.versionNo;
        const v = d.versions.find((x) => x.versionNo === a.submission?.versionNo);
        if (a.status === 'submitted' && isWorking(v?.render.state)) {
          setPolling(true);
          poll(a.tutorialId, gen, 0);
        }
      } catch (e: any) {
        if (gen === genRef.current) setLoadError(e.message || 'Could not load this submission.');
      } finally {
        if (gen === genRef.current) setLoading(false);
      }
    })();
    return () => { genRef.current += 1; stopPoll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  const applyAssignment = (a: VideoAssignment) => { setAssignment(a); onChanged(a); };

  // ---- script editing: saveDraft replaces the whole steps array, so writes
  // are serialised and always send the latest steps (same fix as TutorialsTab).
  const stepsRef = useRef<TutorialVersion['steps']>([]);
  useEffect(() => { stepsRef.current = version?.steps ?? []; }, [version]);
  const writeQueue = useRef<Promise<unknown>>(Promise.resolve());
  const editable = !!assignment && !!version && assignment.status === 'submitted' && version.status === 'draft';

  const saveNarration = async (stepId: string, narration: string) => {
    if (!assignment || !version) return;
    const next = stepsRef.current.map((s) => (s.stepId === stepId ? { ...s, narration } : s));
    stepsRef.current = next;
    setVersions((vs) => vs.map((v) => (v.versionNo === version.versionNo ? { ...v, steps: next } : v)));
    setBusy('script'); setActionError('');
    const run = writeQueue.current.then(async () => {
      const { draft } = await tutorialApi.saveDraft(assignment.tutorialId, stepsRef.current);
      // saveDraft resets the render block server-side -- reflect it so Approve
      // correctly waits for a re-render.
      setVersions((vs) => vs.map((v) => (v.versionNo === draft.versionNo ? draft : v)));
    });
    writeQueue.current = run.catch(() => undefined);
    try { await run; } catch (e: any) {
      setActionError(e.message || 'That script change did not save.');
    } finally { setBusy(null); }
  };

  const reRender = async () => {
    if (!assignment || !versionNo) return;
    setBusy('render'); setActionError('');
    try {
      const { render: r } = await tutorialApi.render(assignment.tutorialId, versionNo);
      setVersions((vs) => vs.map((v) => (v.versionNo === versionNo ? { ...v, render: r } : v)));
      stopPoll();
      setPolling(true);
      poll(assignment.tutorialId, genRef.current, 0);
    } catch (e: any) {
      setActionError(e.message || 'Could not start the render.');
    } finally { setBusy(null); }
  };

  // ---- marketing metadata
  const saveMeta = async (p: { tags?: string[]; adWorthy?: boolean }) => {
    if (!tutorial) return;
    const prev = tutorial;
    setTutorial({ ...tutorial, ...p });
    setBusy('meta'); setActionError('');
    try {
      const { tutorial: t } = await videoTasksApi.updateMeta(tutorial.tutorialId, p);
      setTutorial(t);
    } catch (e: any) {
      setTutorial(prev);
      setActionError(e.message || 'Could not save the marketing tags.');
    } finally { setBusy(null); }
  };
  const addTag = () => {
    const t = tagDraft.trim();
    setTagDraft('');
    if (!t || !tutorial || (tutorial.tags ?? []).includes(t)) return;
    void saveMeta({ tags: [...(tutorial.tags ?? []), t] });
  };

  // ---- decisions
  const approve = async () => {
    if (!assignment) return;
    setBusy('approve'); setActionError('');
    try {
      const { assignment: a } = await videoTasksApi.approve(assignment.assignmentId);
      applyAssignment(a);
      const d = await tutorialApi.get(a.tutorialId).catch(() => null);
      if (d) { setTutorial(d.tutorial); setVersions(d.versions); }
    } catch (e: any) {
      setActionError(friendly(e, 'Could not approve.'));
    } finally { setBusy(null); }
  };

  const requestChanges = async () => {
    if (!assignment || !note.trim()) return;
    setBusy('changes'); setActionError('');
    try {
      const { assignment: a } = await videoTasksApi.requestChanges(assignment.assignmentId, note.trim());
      applyAssignment(a);
      setNote('');
    } catch (e: any) {
      setActionError(friendly(e, 'Could not send the change request.'));
    } finally { setBusy(null); }
  };

  const cancelTask = async () => {
    if (!assignment) return;
    setBusy('cancel'); setActionError('');
    try {
      const { assignment: a } = await videoTasksApi.cancel(assignment.assignmentId);
      applyAssignment(a);
      setShowCancel(false);
    } catch (e: any) {
      setActionError(e.message || 'Could not cancel this task.');
      setShowCancel(false);
    } finally { setBusy(null); }
  };

  const seek = (atMs: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = atMs / 1000;
    void v.play().catch(() => undefined);
  };

  const back = (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1 min-h-[44px] px-2 -ml-2 rounded-lg text-sm font-semibold text-[#DC143C] hover:text-[#9F1239]"
    >
      <ChevronLeft className="w-4 h-4" /> Video tasks
    </button>
  );

  if (loading) return <div>{back}<RubyLoader variant="section" label="Loading submission…" /></div>;
  if (loadError || !assignment) {
    return (
      <div className="space-y-3">
        {back}
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{loadError || 'Not found.'}</p>
      </div>
    );
  }

  const sub = assignment.submission;
  const who = assignment.assignee?.name || assignment.assignee?.email || 'The assignee';
  const firstName = (assignment.assignee?.name || '').split(' ')[0] || who;
  const tenant = tenantName(assignment.assignee?.tenantId);
  const pages = sub?.pages ?? [];
  const highlights = sub?.highlights ?? [];
  const steps = [...(version?.steps ?? [])].sort((a, b) => a.order - b.order);
  const stepStart = (s: TutorialVersion['steps'][number]) => (s.clip?.kind === 'slice' ? s.clip.startMs : 0);
  const transcriptMissing = isTranscriptNote(render?.error) || (steps.length > 0 && steps.every((s) => !s.narration.trim() && !s.transcriptRaw.trim()));
  const activePage = pages.reduce<number>((idx, p, i) => (p.atMs <= currentMs ? i : idx), -1);
  const renderFailed = render?.state === 'failed' && !isTranscriptNote(render.error);
  const canApprove = assignment.status === 'submitted' && ready;
  const closed = assignment.status === 'approved' || assignment.status === 'cancelled';
  const pillLabel = assignment.status === 'submitted' ? 'Waiting for review' : STATUS_LABEL[assignment.status];
  const pillTone = assignment.status === 'submitted' ? 'bg-[#DC143C] text-white' : STATUS_TONE[assignment.status];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        {back}
        <div className="flex flex-wrap items-start justify-between gap-3 mt-1 pb-4">
          <div className="min-w-0">
            <p className="text-[11px] text-gray-400">Certification › Video tasks</p>
            <h2 className="text-xl font-bold text-gray-900">{assignment.title}</h2>
            <p className="text-xs text-gray-500 mt-1">
              {[
                who,
                tenant,
                sub ? `submitted ${new Date(sub.submittedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}` : 'not submitted',
                sub ? formatDuration(sub.durationMs) : '',
                pages.length ? `${pages.length} page${pages.length === 1 ? '' : 's'}` : '',
              ].filter(Boolean).join(' · ')}
            </p>
          </div>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${pillTone}`}>{pillLabel}</span>
        </div>
        <div className="h-[3px] rounded-full bg-gradient-to-r from-[#9F1239] via-[#DC143C] to-[#9F1239]" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,820px)_minmax(0,1fr)] items-start">
        {/* Left: video + pages + marketing */}
        <div className="space-y-4 min-w-0">
          <div className="relative w-full aspect-video rounded-[14px] bg-gray-900 overflow-hidden">
            {ready ? (
              <video
                ref={videoRef}
                controls
                src={tutorialCdnUrl(render!.mp4Key!)}
                poster={render!.posterKey ? tutorialCdnUrl(render!.posterKey) : undefined}
                onTimeUpdate={(e) => setCurrentMs(Math.round(e.currentTarget.currentTime * 1000))}
                className="w-full h-full"
              />
            ) : renderFailed ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
                <p className="text-sm text-red-300">The render failed: {render?.error || 'unknown error'}</p>
                <button
                  type="button"
                  onClick={() => void reRender()}
                  disabled={!!busy}
                  className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-[10px] bg-white text-gray-900 text-sm font-semibold disabled:opacity-40"
                >
                  {busy === 'render' ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="w-4 h-4" />}
                  Render again
                </button>
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-center px-6">
                <RubyLoader variant="section" label="Processing the recording" />
                <p className="text-xs text-white/70">Processing: transcribe → clean → render</p>
                {!polling && assignment.status === 'submitted' && (
                  <button
                    type="button"
                    onClick={() => void reRender()}
                    disabled={!!busy}
                    className="mt-3 inline-flex items-center gap-2 min-h-[44px] px-4 rounded-[10px] bg-white/10 text-white text-sm font-semibold hover:bg-white/20 disabled:opacity-40"
                  >
                    {busy === 'render' ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="w-4 h-4" />}
                    Render now
                  </button>
                )}
              </div>
            )}
            {version?.voice === 'original' && (
              <span className="absolute top-3 right-3 text-[11px] text-white px-2 py-1 rounded-md bg-white/15 pointer-events-none">Original voice</span>
            )}
          </div>

          {pages.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Pages visited — click to jump</p>
              <div className="flex flex-wrap gap-2">
                {pages.map((p, i) => (
                  <button
                    key={`${p.atMs}-${i}`}
                    type="button"
                    onClick={() => seek(p.atMs)}
                    disabled={!ready}
                    className={`min-h-[44px] px-3 rounded-[10px] text-[12.5px] border transition disabled:opacity-60 ${
                      i === activePage ? 'border-[#DC143C] bg-[#FFF1F3] text-[#9F1239]' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <span className="font-bold">{mmss(p.atMs)}</span> {p.label || p.route}
                  </button>
                ))}
              </div>
            </div>
          )}

          {tutorial && (
            <div className="border border-gray-200 rounded-[14px] p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[15px] font-bold text-gray-900">Marketing source</p>
                  <p className="text-xs text-gray-500">Kept forever, even after publish — the raw material for AI ads.</p>
                </div>
                {version?.source?.rawKey && !tutorial.purgedAt && (
                  <span className="text-[11.5px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">Raw voice kept</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {(tutorial.tags ?? []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs pl-2.5 pr-1 min-h-[32px] rounded-full bg-gray-100 text-gray-700">
                    {t}
                    <button
                      type="button"
                      aria-label={`Remove tag ${t}`}
                      disabled={busy === 'meta'}
                      onClick={() => void saveMeta({ tags: (tutorial.tags ?? []).filter((x) => x !== t) })}
                      className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  onBlur={addTag}
                  placeholder="+ Tag"
                  aria-label="Add a tag"
                  className="text-xs px-2.5 min-h-[32px] w-24 rounded-full border border-dashed border-gray-300 text-gray-700 placeholder:text-gray-500 focus:outline-none focus:border-[#DC143C]"
                />
              </div>
              <div className="flex items-center gap-2.5 text-[13px] text-gray-700">
                <button
                  type="button"
                  role="switch"
                  aria-checked={tutorial.adWorthy}
                  aria-label="Good for ads"
                  disabled={busy === 'meta'}
                  onClick={() => void saveMeta({ adWorthy: !tutorial.adWorthy })}
                  className="min-h-[44px] flex items-center"
                >
                  <span className={`relative inline-block w-10 h-6 rounded-full transition ${tutorial.adWorthy ? 'bg-[#DC143C]' : 'bg-gray-300'}`}>
                    <span className={`absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white transition-all ${tutorial.adWorthy ? 'right-[3px]' : 'left-[3px]'}`} />
                  </span>
                </button>
                <span className="flex flex-wrap items-center gap-1.5">
                  Good for ads
                  {highlights.length > 0 ? (
                    <>
                      <span>— {highlights.length} highlight moment{highlights.length === 1 ? '' : 's'} marked</span>
                      {highlights.map((h, i) => (
                        <button
                          key={`${h.atMs}-${i}`}
                          type="button"
                          onClick={() => seek(h.atMs)}
                          disabled={!ready}
                          className="min-h-[32px] px-2 rounded-md bg-[#FFF1F3] text-[#9F1239] text-xs font-semibold disabled:opacity-60"
                        >
                          {mmss(h.atMs)}
                        </button>
                      ))}
                    </>
                  ) : <span className="text-gray-400">— no highlights marked</span>}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Right: script + checklist + decision */}
        <div className="space-y-3.5 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex bg-gray-100 rounded-[10px] p-1 gap-1" role="tablist">
              {([['script', 'Cleaned script'], ['raw', 'Raw transcript'], ['brief', 'Brief']] as const).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={view === id}
                  onClick={() => setView(id)}
                  className={`min-h-[40px] px-3.5 rounded-lg text-[13px] transition ${
                    view === id ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {view === 'script' && (
              <span className="text-xs text-gray-500 flex items-center gap-1.5">
                {busy === 'script' && <RubyGemMark size={12} drawing />}
                {editable ? 'AI-cleaned · edit inline' : 'AI-cleaned'}
              </span>
            )}
          </div>

          <div className="border border-gray-200 rounded-[14px] px-4 py-1">
            {view === 'brief' ? (
              <p className="py-3 text-[13px] leading-relaxed text-gray-700 whitespace-pre-wrap">{assignment.brief || 'No brief was written for this topic.'}</p>
            ) : steps.length === 0 || transcriptMissing ? (
              <p className="py-3 text-[13px] text-gray-400">Transcript unavailable — the video still plays and can be approved.</p>
            ) : (
              steps.map((s) => (
                <div key={s.stepId} className="flex gap-3 py-3 border-b border-gray-100 last:border-b-0">
                  <span className="text-xs font-bold text-[#9F1239] w-10 flex-shrink-0 pt-0.5">{mmss(stepStart(s))}</span>
                  {view === 'script' && editable ? (
                    <textarea
                      defaultValue={s.narration}
                      rows={2}
                      aria-label={`Script at ${mmss(stepStart(s))}`}
                      onBlur={(e) => { if (e.target.value !== s.narration) void saveNarration(s.stepId, e.target.value); }}
                      className="flex-1 text-[13px] leading-normal text-gray-700 rounded-md border border-transparent hover:border-gray-200 focus:border-[#DC143C] focus:outline-none px-1.5 py-0.5 resize-y"
                    />
                  ) : (
                    <span className="text-[13px] leading-normal text-gray-700 whitespace-pre-wrap">
                      {(view === 'script' ? s.narration : s.transcriptRaw) || <span className="text-gray-400">—</span>}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
          {editable && view === 'script' && !transcriptMissing && steps.length > 0 && (
            <p className="text-[11.5px] text-gray-500">Editing the script resets the render — use “Render now” before approving.</p>
          )}

          {assignment.keyPoints.length > 0 && (
            <div className="bg-gray-50 rounded-[14px] px-4 py-3.5 space-y-1">
              <p className="text-[13px] font-bold text-gray-900">Key points from the brief</p>
              {assignment.keyPoints.map((p) => {
                const on = ticked.has(p);
                return (
                  <button
                    key={p}
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    onClick={() => setTicked((t) => { const n = new Set(t); if (on) n.delete(p); else n.add(p); return n; })}
                    className="w-full flex items-center gap-2 min-h-[44px] text-[13px] text-left"
                  >
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${on ? 'bg-emerald-100 text-emerald-700' : 'border border-gray-300 bg-white'}`}>
                      {on && <Check className="w-3 h-3" />}
                    </span>
                    <span className="text-gray-900">{p}</span>
                  </button>
                );
              })}
            </div>
          )}

          {!closed && (
            <div>
              <label htmlFor="vt-note" className="block text-[12.5px] font-semibold text-gray-700 mb-1.5">
                Note to {firstName} (sent if you request changes)
              </label>
              <textarea
                id="vt-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full rounded-[10px] border border-gray-300 px-3 py-2.5 text-[13px] resize-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/15 focus:outline-none"
              />
            </div>
          )}

          {assignment.rewardTokens > 0 && (
            <div className="border border-dashed border-gray-300 rounded-[14px] px-3.5 py-3 flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13.5px] font-bold text-gray-900">
                    Reward: {assignment.rewardTokens.toLocaleString()} Ruby Tokens → {tenant || 'tenant'}
                  </span>
                  <span className="text-[10.5px] font-extrabold px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e]">UNDER CONSTRUCTION</span>
                </div>
                <p className="text-xs text-gray-500">
                  {assignment.status === 'approved'
                    ? 'Offered in the assignment email. Token process links here later.'
                    : 'Unlocks after approval'}
                </p>
              </div>
              <button
                type="button"
                disabled
                aria-disabled="true"
                title="Token grants are coming soon"
                className="min-h-[44px] px-4 rounded-[10px] border border-gray-200 bg-gray-50 text-gray-400 text-[13.5px] font-semibold cursor-not-allowed"
              >
                Grant {assignment.rewardTokens.toLocaleString()} tokens
              </button>
            </div>
          )}

          {actionError && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">{actionError}</p>
          )}

          {!closed && (
            <div className="flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={() => setShowCancel(true)}
                disabled={!!busy}
                className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-[10px] text-sm text-red-600 hover:bg-red-50 disabled:opacity-40"
              >
                <Ban className="w-4 h-4" /> Cancel task
              </button>
              <button
                type="button"
                onClick={() => void requestChanges()}
                disabled={!!busy || !note.trim() || assignment.status !== 'submitted'}
                className="ml-auto inline-flex items-center gap-2 min-h-[48px] px-4 rounded-[10px] border border-gray-300 bg-white text-sm text-gray-900 hover:border-gray-900 disabled:opacity-40"
              >
                {busy === 'changes' && <RubyGemMark size={16} drawing color="currentColor" />}
                Request changes
              </button>
              <button
                type="button"
                onClick={() => void approve()}
                disabled={!!busy || !canApprove}
                title={!canApprove ? 'Available once the submission has rendered' : undefined}
                className="inline-flex items-center gap-2 min-h-[48px] px-5 rounded-[10px] bg-[#DC143C] text-white text-sm font-semibold hover:bg-[#9F1239] disabled:opacity-40"
              >
                {busy === 'approve' && <RubyGemMark size={16} drawing color="currentColor" />}
                Approve &amp; publish
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showCancel}
        title="Cancel this video task?"
        tone="danger"
        confirmLabel="Cancel task"
        busy={busy === 'cancel'}
        onConfirm={() => void cancelTask()}
        onClose={() => setShowCancel(false)}
      >
        <p>{who} can no longer change or resubmit it, and it will not be published. This cannot be undone.</p>
      </ConfirmDialog>
    </div>
  );
}
